import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Settings } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { RubyIcon } from "@/components/Icons";
import Avatar from "@/components/Avatar";

const UPI_APPS = ["GPay", "PhonePe", "Paytm", "UPI"];

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function WeekGraph({ week }) {
  const max = Math.max(...week.map((d) => d.total), 1);
  const pts = week.map((d, i) => `${20 + i * 46},${86 - (d.total / max) * 70}`).join(" ");
  return (
    <svg viewBox="0 0 320 110" className="w-full" data-testid="earnings-graph">
      {[0, 1, 2].map((i) => <line key={i} x1="14" x2="306" y1={16 + i * 35} y2={16 + i * 35} stroke="rgba(255,255,255,0.07)" strokeWidth="1" />)}
      <polyline points={pts} fill="none" stroke="url(#eg)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 6px rgba(138,43,226,0.8))" }} />
      {week.map((d, i) => (
        <g key={d.day}>
          <circle cx={20 + i * 46} cy={86 - (d.total / max) * 70} r="3" fill="#E6B84C" />
          <text x={20 + i * 46} y="104" textAnchor="middle" fontSize="9" fill="#71717a" fontWeight="700">{d.day}</text>
        </g>
      ))}
      <defs><linearGradient id="eg" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#8A2BE2" /><stop offset="100%" stopColor="#E6B84C" /></linearGradient></defs>
    </svg>
  );
}

export default function Earnings() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showKyc, setShowKyc] = useState(false);
  const [kyc, setKyc] = useState({ full_name: "", pan: "", upi: "" });

  const load = () => { setError(false); api.get("/wallet/earnings").then(({ data: d }) => setData(d)).catch(() => setError(true)); };
  useEffect(() => { load(); }, []);

  const withdraw = async () => {
    if (data.kyc_status !== "verified") { setShowKyc(true); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post("/payouts/request", { diamonds: data.diamonds });
      toast.success(`Withdrawal of ₹${res.payout.net_inr} requested — processed within 24 hours`);
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const submitKyc = async () => {
    setBusy(true);
    try {
      await api.post("/payouts/kyc", kyc);
      toast.success("KYC submitted for review ✅ You can withdraw once verified");
      setShowKyc(false);
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) return (
    <div className="min-h-screen max-w-md mx-auto flex flex-col items-center justify-center gap-3" data-testid="earnings-error">
      <p className="text-zinc-400 text-sm">Couldn't load earnings</p>
      <button data-testid="earnings-retry" onClick={load} className="gradient-primary text-white text-xs font-bold px-5 py-2 rounded-full">Retry</button>
    </div>
  );
  if (!data) return <div className="min-h-screen max-w-md mx-auto flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-[#E6B84C] border-t-transparent animate-spin" /></div>;

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6 pb-10" data-testid="earnings-page">
      <div className="flex items-center justify-between mb-5">
        <button data-testid="earnings-back" onClick={() => navigate(-1)} className="text-[#E6B84C]"><ArrowLeft size={20} /></button>
        <h1 className="font-royal text-2xl gradient-gold-text font-bold tracking-wide">EARNINGS</h1>
        <button data-testid="earnings-settings" onClick={() => navigate("/settings")} className="text-[#E6B84C]"><Settings size={18} /></button>
      </div>

      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center py-4">
        <div className="relative">
          <span className="absolute -inset-6 rounded-full blur-2xl bg-cyan-400/25 pointer-events-none" />
          <div className="relative w-28 h-28 rounded-full border-2 border-[#E6B84C]/50 flex items-center justify-center shadow-[0_0_30px_rgba(0,229,255,0.3)]">
            <RubyIcon size={52} />
          </div>
        </div>
        <p className="text-white font-extrabold text-2xl mt-3 font-display" data-testid="earnings-diamonds">{data.diamonds.toLocaleString("en-IN")} Rubies</p>
        <p className="text-[#E6B84C] text-sm font-bold" data-testid="earnings-inr">≈ ₹{Number(data.inr_estimate).toLocaleString("en-IN")}</p>
      </motion.div>

      <div className="glass rounded-3xl p-4 mt-2">
        <div className="flex items-center justify-between mb-1">
          <p className="text-white text-xs font-bold">This Week</p>
          <span className="text-sm">👑</span>
        </div>
        <WeekGraph week={data.week} />
      </div>

      <h2 className="font-royal text-lg gradient-gold-text font-bold mt-6 mb-3">Recent Gifts</h2>
      <div className="space-y-1.5" data-testid="recent-gifts-list">
        {data.recent.length === 0 && <p className="text-zinc-500 text-xs text-center py-4">No gifts received yet — go live to earn rubies!</p>}
        {data.recent.map((r, i) => (
          <div key={i} className="glass rounded-2xl px-3.5 py-2.5 flex items-center gap-3">
            <Avatar src={r.sender?.avatar} size={36} />
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-bold truncate">{r.sender?.name || "A fan"}</p>
              <p className="text-[10px] text-zinc-500 truncate">{r.gift || "Gift"}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-rose-300 text-xs font-extrabold flex items-center gap-1 justify-end">+{r.amount.toLocaleString("en-IN")} <RubyIcon size={11} /></p>
              <p className="text-[9px] text-zinc-600">{timeAgo(r.created_at)}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-royal text-lg gradient-gold-text font-bold mt-6 mb-3">Withdraw to UPI</h2>
      <div className="flex gap-2 mb-4">
        {UPI_APPS.map((u) => (
          <span key={u} className="flex-1 glass rounded-xl py-2.5 text-center text-[11px] font-bold text-zinc-200">{u}</span>
        ))}
      </div>

      {showKyc && (
        <div className="glass rounded-2xl p-4 mb-4 border border-[#E6B84C]/30" data-testid="kyc-form">
          <p className="text-[#E6B84C] text-xs font-bold mb-3">Complete KYC to withdraw</p>
          <input data-testid="kyc-name" value={kyc.full_name} onChange={(e) => setKyc((k) => ({ ...k, full_name: e.target.value }))} placeholder="Full name (as per PAN)"
            className="w-full bg-[#131A2A] border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white outline-none mb-2 placeholder:text-zinc-500" />
          <input data-testid="kyc-pan" value={kyc.pan} onChange={(e) => setKyc((k) => ({ ...k, pan: e.target.value.toUpperCase() }))} placeholder="PAN number"
            className="w-full bg-[#131A2A] border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white outline-none mb-2 placeholder:text-zinc-500" />
          <input data-testid="kyc-upi" value={kyc.upi} onChange={(e) => setKyc((k) => ({ ...k, upi: e.target.value }))} placeholder="UPI ID (name@bank)"
            className="w-full bg-[#131A2A] border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white outline-none mb-3 placeholder:text-zinc-500" />
          <button data-testid="kyc-submit" onClick={submitKyc} disabled={!kyc.full_name || !kyc.pan || busy}
            className="w-full gradient-primary text-white text-xs font-bold py-3 rounded-full disabled:opacity-40">Submit KYC</button>
        </div>
      )}

      <button data-testid="withdraw-button" onClick={withdraw} disabled={busy || data.diamonds < data.min_payout_diamonds}
        className="w-full bg-gradient-to-r from-[#FDE08B] via-[#FF9E4D] to-[#FF007F] text-white font-extrabold font-display py-4 rounded-full shadow-[0_0_24px_rgba(255,0,127,0.45)] active:scale-[0.98] transition-transform disabled:opacity-50 tracking-wide">
        {busy ? "PROCESSING…" : `WITHDRAW ₹${Number(data.net_inr_estimate).toLocaleString("en-IN")}`}
      </button>
      <p className="text-zinc-600 text-[10px] text-center mt-2">
        {data.diamonds < data.min_payout_diamonds
          ? `Minimum ${data.min_payout_diamonds.toLocaleString("en-IN")} rubies needed to withdraw`
          : `You receive ₹${Number(data.net_inr_estimate).toLocaleString("en-IN")} after ${data.commission_pct}% platform fee · Processed within 24 hours`}
      </p>

      {data.payouts.length > 0 && (
        <div className="mt-5 space-y-1.5" data-testid="payout-history">
          {data.payouts.map((p) => (
            <div key={p.id} className="flex items-center justify-between bg-[#131A2A] rounded-xl px-3.5 py-2.5">
              <p className="text-xs text-white font-semibold">₹{p.net_inr}</p>
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : p.status === "rejected" ? "bg-rose-500/20 text-rose-300" : "bg-amber-500/20 text-amber-300"}`}>{p.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
